'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Donation,
  Organization,
  Project,
  ProjectNews,
  ProjectPhoto,
  ProjectSettingsModeGroupEnum,
  ProjectStateStatusEnum,
  Reward,
  Subproject,
  UserRolesEnum,
} from '@/app/api';
import { Routes } from '@/app/enums';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { SideBarContentEnum, useDisplaySettings } from '@/app/pages/ProjectDetail/hooks/useDisplaySettings';
import { useInitialScrollToTabs } from '@/app/pages/ProjectDetail/hooks/useInitialScrollToTabs';
import { useScrollToTabsIfNeeded } from '@/app/pages/ProjectDetail/hooks/useScrollToTabsIfNeeded';
import { useSetSuccessBackground } from '@/app/pages/ProjectDetail/hooks/useSetSuccessBackground';
import { ProjectTabs as ProjectTabsEnum } from '@/app/pages/ProjectDetail/types';

import { ChatBot } from '@/app/components/ChatBox/ChatBox';
import { CookieDevtool } from '@/app/components/CookieDevtool/CookieDevtool';
import GoodHabitBarWidget from '@/app/components/GoodHabitBarWidget/GoodHabitBarWidget';
import LanguageSwitch from '@/app/components/LanguageSwitch/LanguageSwitch';
import ProjectPageHeader from '@/app/components/ProjectPageHeader/ProjectPageHeader';
import ProjectPageHeaderForAbTest from '@/app/components/ProjectPageHeaderForAbTest/ProjectPageHeaderForAbTest';
import ProjectsVerificationDialog from '@/app/components/ProjectsVerificationDialog/ProjectsVerificationDialog';
import RewardPickerDrawer from '@/app/components/RewardPickerDrawer/RewardPickerDrawer';
import ShareDialog from '@/app/components/ShareDialog/ShareDialog';
import { SupportStoryWidget } from '@/app/components/SupportStoryWidget/SupportStoryWidget';
import { REWARDS_IS_PICKER_OPEN_KEY } from '@/app/constants';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useProjectDetailViewAnalytics } from '@/app/hooks/useAnalytics/events/project-detail/project-detail-view';
import { useIsUserInNewHeaderLayoutGroup } from '@/app/hooks/useIsUserInNewHeaderLayoutGroup';
import { useIsUserInSubProjectBoxReplaceGroup } from '@/app/hooks/useIsUserInSubProjectBoxReplaceGroup';
import { useLoadMyGoodHabit } from '@/app/hooks/useLoadMyGoodHabit';
import { DonationsSideBar } from '@/app/pages/ProjectDetail/components/DonationsSideBar';
import { MoreStories } from '@/app/pages/ProjectDetail/components/MoreStories';
import { ProjectClosedWidget } from '@/app/pages/ProjectDetail/components/ProjectClosedWidget';
import { ProjectTabContent } from '@/app/pages/ProjectDetail/components/ProjectTabContent';
import ProjectTabs from '@/app/pages/ProjectDetail/components/ProjectTabs';
import { RewardsMobileWidget, RewardsSideBar } from '@/app/pages/ProjectDetail/components/RewardsSideBar';
import { TopicLink } from '@/app/pages/ProjectDetail/components/TopicLink';
import { useTabs } from '@/app/pages/ProjectDetail/hooks/useTabs';
import { envs, NodeEnv } from '@/app/utils/envs';
import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

interface ProjectDetailProps {
  project: Project;
  images: ProjectPhoto[];
  subProjectsCount?: number;
  donations?: Donation[];
  projectNews: ProjectNews[];
  projectNewsTotalCount?: number;
  moreProjects: Project[];
  rewards?: Reward[];
  organizationAutorOfProject?: Organization;
  subProjects: Subproject[];
}

const ProjectDetail = ({
  project,
  images,
  donations,
  projectNews,
  projectNewsTotalCount,
  moreProjects,
  subProjectsCount,
  rewards,
  organizationAutorOfProject,
  subProjects,
}: ProjectDetailProps) => {
  const {
    projectDetail: { pushShareModalOpenEvent },
  } = useAnalytics();

  const { activeGoodHabit } = useLoadMyGoodHabit();

  useProjectDetailViewAnalytics();

  const searchParams = useSearchParams();
  const urlTabId = searchParams.get('tab');

  const defaultTabId =
    urlTabId && Object.values(ProjectTabsEnum).includes(urlTabId as ProjectTabsEnum)
      ? (urlTabId as ProjectTabsEnum)
      : ProjectTabsEnum.PROJECT;

  const projectHeaderRef = useRef<HTMLDivElement>(null);
  const tabsAnchorRef = useRef<HTMLDivElement>(null);
  const [isTabsElementFixed, setIsTabsElementFixed] = useState(false);
  const [isRewardPickerOpen, setIsRewardPickerOpen] = useState(
    searchParams.get(REWARDS_IS_PICKER_OPEN_KEY) === 'true' &&
      project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus,
  );
  const { currentUser } = useCurrentUser();

  const isUserInNewHeaderLayoutGroup = useIsUserInNewHeaderLayoutGroup();
  const isUserInSubProjectBoxReplaceGroup = useIsUserInSubProjectBoxReplaceGroup();
  const router = useRouter();
  const [activeTabId, setActiveTabId] = useState(defaultTabId);

  const { t } = useTranslation();

  useEffect(() => {
    const hashUrlParam = window.location.hash;

    if (hashUrlParam.includes('news-')) {
      setActiveTabId(ProjectTabsEnum.NEWS);
    }
  }, []);

  useEffect(() => {
    if (isRewardPickerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isRewardPickerOpen]);

  const handleCloseRewardPicker = () => {
    if (searchParams.get(REWARDS_IS_PICKER_OPEN_KEY) === 'true') {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(REWARDS_IS_PICKER_OPEN_KEY);
      router.push(`?${params.toString()}`);
    }
    setIsRewardPickerOpen(false);
  };

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const [isVerificationNoteDialogOpen, setIsVerificationNoteDialogOpen] = useState(false);
  const tabs = useTabs({ project, projectNewsLength: projectNewsTotalCount, subProjectsCount });

  const scrollToTabsIfNeeded = useScrollToTabsIfNeeded(tabsAnchorRef);

  const displaySettings = useDisplaySettings({ project });

  const isClosedOrFinished =
    project.state.status === ProjectStateStatusEnum.Closed || project.state.status === ProjectStateStatusEnum.Finished;

  useSetSuccessBackground({
    shouldDisplaySuccessBackground: !!project.state?.actualSuccess && isClosedOrFinished,
  });
  useInitialScrollToTabs(tabsAnchorRef, setActiveTabId);

  const handleShareButtonClick = () => {
    setIsShareDialogOpen(true);
    pushShareModalOpenEvent();
  };

  const handleCloseShareDialog = useCallback(() => {
    setIsShareDialogOpen(false);
  }, [setIsShareDialogOpen]);

  const handleVerificationNoteClick = useCallback(() => {
    setIsVerificationNoteDialogOpen(true);
  }, []);

  const handleCloseVerificationNoteDialog = useCallback(() => {
    setIsVerificationNoteDialogOpen(false);
  }, [setIsVerificationNoteDialogOpen]);

  const handleSelectTab = useCallback(
    (tabId: ProjectTabsEnum) => {
      setActiveTabId(tabId);
      scrollToTabsIfNeeded();
    },
    [setActiveTabId, scrollToTabsIfNeeded],
  );

  const handleToggleTabsElementFixed = useCallback(
    (isFixed: boolean) => {
      setIsTabsElementFixed(isFixed);
    },
    [setIsTabsElementFixed],
  );

  const handleMoreDonorsButtonClick = useCallback(() => {
    setActiveTabId(ProjectTabsEnum.DONORS);
    scrollToTabsIfNeeded();
  }, [scrollToTabsIfNeeded]);

  const handleReadMoreClick = useCallback(() => {
    projectHeaderRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const adminDetailLink = `${envs.redirectUrl}/${Routes.PROJECT_ADMIN}/${project.id}`;

  return (
    <div className="w-full min-h-[100vh] pb-[40px]">
      {activeGoodHabit && <GoodHabitBarWidget activeGoodHabit={activeGoodHabit} className="mt-[16px] mb-[40px]" />}
      <div className="w-full default-page-max-width mx-auto px-[24px]">
        <LanguageSwitch className={classNames('mb-[16px] md:flex', isUserInNewHeaderLayoutGroup && 'hidden')} />
        {!project.state.statusIsVisibleOnWeb && (
          <div className="text-[24px] nezwald-font border-y py-[8px] md:py-[32px] mb-[32px] md:mb-[56px]">
            {t('pages.collection.projectPreviewInfoMessage')}
          </div>
        )}
        {displaySettings.isProjectClosedWidgetDisplayed && (
          <ProjectClosedWidget
            coverImage={project.content.mainPhoto || images?.[0]}
            project={project}
            moreProjects={moreProjects}
            handleReadMoreClick={handleReadMoreClick}
          />
        )}

        {isUserInNewHeaderLayoutGroup ? (
          <ProjectPageHeaderForAbTest
            adminDetailLink={adminDetailLink}
            matching={project.donationMatching || undefined}
            isUserAdmin={currentUser?.roles.includes(UserRolesEnum.Admin)}
            onCommercialDonateClick={() => setIsRewardPickerOpen(true)}
            isCommercial={project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus}
            title={project.content.currentName}
            currentAmountInCents={project.statistics.currentAmountInCents}
            goalInCents={project.statistics.targetAmountInCents ?? undefined}
            donationsCount={project.statistics.donationsCount}
            numericId={project.numericId}
            images={images}
            coverImage={{
              small: project.content.mainPhoto?.sizes.smallCropped.path || images[0].sizes.smallCropped.path,
              large: project.content.mainPhoto?.sizes.fullCropped.path || images[0].sizes.fullCropped.path,
            }}
            mainVideoSlug={project.content.texts.mainVideo ?? undefined}
            milestones={project.milestones}
            endsAt={project.timestamps?.scheduledEnding ?? undefined}
            onShareButtonClick={handleShareButtonClick}
            onVerificationNoteClick={handleVerificationNoteClick}
            innerRef={projectHeaderRef}
            isProjectLongTerm={project.settings.isLongTerm}
            activeGoodHabitAmount={activeGoodHabit?.donation.amountInCents}
          />
        ) : (
          <ProjectPageHeader
            adminDetailLink={adminDetailLink}
            matching={project.donationMatching || undefined}
            isUserAdmin={currentUser?.roles.includes(UserRolesEnum.Admin)}
            onCommercialDonateClick={() => setIsRewardPickerOpen(true)}
            isCommercial={project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus}
            title={project.content.currentName}
            currentAmountInCents={project.statistics.currentAmountInCents}
            goalInCents={project.statistics.targetAmountInCents ?? undefined}
            donationsCount={project.statistics.donationsCount}
            numericId={project.numericId}
            images={images}
            coverImage={{
              small: project.content.mainPhoto?.sizes.smallCropped.path || images[0].sizes.smallCropped.path,
              large: project.content.mainPhoto?.sizes.fullCropped.path || images[0].sizes.fullCropped.path,
            }}
            mainVideoSlug={project.content.texts.mainVideo ?? undefined}
            milestones={project.milestones}
            endsAt={project.timestamps?.scheduledEnding ?? undefined}
            onShareButtonClick={handleShareButtonClick}
            onVerificationNoteClick={handleVerificationNoteClick}
            innerRef={projectHeaderRef}
            isProjectLongTerm={project.settings.isLongTerm}
            activeGoodHabitAmount={activeGoodHabit?.donation.amountInCents}
          />
        )}
      </div>
      {displaySettings.sideBarContent === SideBarContentEnum.REWARDS && rewards && (
        <>
          <RewardsMobileWidget numericId={project.numericId} rewards={rewards} className="md:hidden" />
        </>
      )}
      <div ref={tabsAnchorRef} />
      <ProjectTabs
        onCommercialDonationClick={() => setIsRewardPickerOpen(true)}
        projectModeGroup={project.settings.modeGroup}
        tabs={tabs}
        activeTabId={activeTabId}
        donationLink={`/${Routes.DONATION}/${project.numericId}`}
        isElementFixed={isTabsElementFixed}
        onToggleElementFixed={handleToggleTabsElementFixed}
        onTabChange={handleSelectTab}
      />
      <div className="w-full default-page-max-width mx-auto px-[24px]">
        <div className="flex flex-col lg:flex-row pb-[32px] lg:gap-[24px] xxl:gap-[124px]">
          <div className="w-full grow lg:max-w-[640px]">
            <ProjectTabContent
              activeSubProjects={subProjects}
              isSubprojectBannerVisible={displaySettings.isSubprojectBannerVisible}
              organizationAutorOfProject={organizationAutorOfProject}
              project={project}
              activeTabId={activeTabId}
              projectNews={projectNews}
              handleSelectTab={handleSelectTab}
            />
            {activeTabId !== ProjectTabsEnum.SUBPROJECT && displaySettings.isSupportProjectBannerVisible && (
              <SupportStoryWidget
                onCommercialDonateClick={() => setIsRewardPickerOpen(true)}
                className="mt-[72px]"
                activeGoodHabitAmount={activeGoodHabit?.donation.amountInCents}
              />
            )}
          </div>
          <div className="w-[360px]">
            {displaySettings.sideBarContent === SideBarContentEnum.MESSAGES && (
              <DonationsSideBar donations={donations} onMoreDonorsButtonClick={handleMoreDonorsButtonClick} />
            )}
            {displaySettings.sideBarContent === SideBarContentEnum.REWARDS && rewards && (
              <>
                <RewardsSideBar
                  numericId={project.numericId}
                  rewards={rewards}
                  className="hidden md:block min-w-[360px]"
                />
              </>
            )}
          </div>
        </div>
        <div className="mb-[64px]">
          {project.settings.taxonomy.topic && <TopicLink topic={project.settings.taxonomy.topic} />}
          {!!displaySettings.footnoteText && <p className="mt-[16px]">{displaySettings.footnoteText}</p>}
        </div>

        {isUserInSubProjectBoxReplaceGroup && (
          <div className="h-[9px] ml-[-24px] mr-[-24px] md:ml-[-24px] md:mr-[-24px]   bg-neutral-light  mb-[64px]" />
        )}
        <MoreStories title={displaySettings.moreProjectsHeading} projects={moreProjects} />
      </div>
      <ShareDialog
        defaultEmailValue={currentUser?.email ?? ''}
        project={project}
        isOpen={isShareDialogOpen}
        onClose={handleCloseShareDialog}
      />
      <RewardPickerDrawer
        numericId={project.numericId}
        isOpen={isRewardPickerOpen}
        rewards={rewards || []}
        onClose={handleCloseRewardPicker}
        isDisabled={project.state.status !== ProjectStateStatusEnum.Active}
      />
      <ProjectsVerificationDialog isOpen={isVerificationNoteDialogOpen} onClose={handleCloseVerificationNoteDialog} />
      <ChatBot />
      {[NodeEnv.QaDev, NodeEnv.Local].includes(envs.businessEnv) && <CookieDevtool />}
    </div>
  );
};

export default ProjectDetail;
